import { RotateCw, WifiOff } from 'lucide-react';
import { Icon } from './Icon';
import { useT } from '../../i18n';
import { COMMON } from '../../i18n/common';

/**
 * «Ошибка загрузки — повторить» (agroai-6al.35, журнал №293).
 *
 * Отдельный компонент, а не `EmptyState` с другим текстом: пустое состояние
 * говорит о ДАННЫХ («открытых кампаний нет»), ошибка — о СВЯЗИ («сервер не
 * ответил»). Пока они рисовались одним шаблоном, недоступный сервер на экране
 * выглядел как «данных нет», и человек делал вывод о районе по сбою сети.
 *
 * Правило для блоков: запрос упал (`state === 'error'`) — `LoadError` с
 * кнопкой «Повторить»; запрос прошёл и пусто — `EmptyState` с причиной.
 */
export interface LoadErrorProps {
  /** Что не загрузилось, в винительном падеже: «темп кампании». */
  what: string;
  /** Ошибка запроса: показывается её текст, если он есть. */
  error?: { message?: string } | string | null;
  /** Повторить запрос; без него кнопки нет. */
  onRetry?: () => void;
  compact?: boolean;
}

/** Текст ошибки запроса или `null`. */
function messageOf(error: LoadErrorProps['error']): string | null {
  if (!error) return null;
  if (typeof error === 'string') return error;
  return error.message ?? null;
}

export function LoadError({ what, error, onRetry, compact }: LoadErrorProps) {
  const message = messageOf(error);
  const t = useT();
  return (
    <div className={`empty load-error ${compact ? 'empty--compact' : ''}`} role="alert">
      <span className="empty__icon load-error__icon">
        <Icon icon={WifiOff} size={20} />
      </span>
      <span className="empty__title">{t(COMMON.loadErrorTitle, { what })}</span>
      <span className="empty__reason">
        {t(COMMON.loadErrorReason)}{message ? ` ${message}` : ''}
      </span>
      {onRetry ? (
        <button type="button" className="astra-button" onClick={onRetry}>
          <Icon icon={RotateCw} size={16} /> {t(COMMON.retry)}
        </button>
      ) : null}
    </div>
  );
}

/** Запрос упал и показать нечего: прежнего результата нет. */
export function failed(req: { state: string; data: unknown }): boolean {
  return req.state === 'error' && (req.data === null || req.data === undefined);
}
